export function normalizeImagePath(path: string): string {
    if (!path) return '';

    // Remove any 'public' prefix if it exists
    path = path.replace(/^public\//, '');

    // Ensure path starts with '/'
    return path.startsWith('/') ? path : `/${path}`;
}

export interface ImagePaths {
    full: string;
    thumbnail: string;
    webp: string;
    thumbnailWebp: string;
}

const EMPTY: ImagePaths = { full: '', thumbnail: '', webp: '', thumbnailWebp: '' };

/* This module used to log five lines on every call, and it is called once
   per card — loading the collages grid alone put ~150 lines into the
   console of every visitor. */
export const getImagePaths = (imagePath: string): ImagePaths => {
    const normalizedPath = normalizeImagePath(imagePath);

    if (!normalizedPath) return EMPTY;

    const cleanPath = normalizedPath.replace(/^\//, '');
    const pathInfo = cleanPath.match(
        /^(images\/(thumbnails\/)?(books|collages))\/([^/]+)\.([^.]+)$/
    );

    // Not a recognised images/{books,collages} path — use it as-is for
    // every variant rather than returning empty strings.
    if (!pathInfo) {
        const asIs = `/${cleanPath}`;
        return { full: asIs, thumbnail: asIs, webp: asIs, thumbnailWebp: asIs };
    }

    const [, basePath, , category, fileName, ext] = pathInfo;

    const paths: ImagePaths = {
        full: `/${cleanPath}`,
        thumbnail: `/images/thumbnails/${category}/${fileName}.${ext}`,
        webp: `/${basePath}/${fileName}.webp`,
        thumbnailWebp: `/images/thumbnails/${category}/${fileName}.webp`
    };

    (Object.keys(paths) as (keyof ImagePaths)[]).forEach(key => {
        paths[key] = paths[key].replace(/\/+/g, '/');
    });

    return paths;
};

/* WebP support is a property of the browser, not of the image, so the
   canvas probe only needs to run once per page rather than once per
   image. */
let webPSupport: boolean | null = null;

const supportsWebP = (): boolean => {
    if (webPSupport !== null) return webPSupport;
    if (typeof document === 'undefined') return false;
    webPSupport =
        document
            .createElement('canvas')
            .toDataURL('image/webp')
            .indexOf('data:image/webp') === 0;
    return webPSupport;
};

/** Picks the best available format for the current browser. */
export const getBestImagePath = (paths: ImagePaths, isThumb = false): string => {
    if (supportsWebP()) {
        return isThumb ? paths.thumbnailWebp : paths.webp;
    }
    return isThumb ? paths.thumbnail : paths.full;
};

/** Whether a given context should use the small (~40KB) version. */
export const shouldUseThumbnail = (
    context: 'grid' | 'preview' | 'detail' | 'lightbox'
): boolean => ['grid', 'preview'].includes(context);
